import React, { useRef, useEffect, useMemo } from 'react';
import { BarChartPro } from '@mui/x-charts-pro/BarChartPro';
import type { GraphDistribution } from '../GraphBox.tsx';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import html2canvas from 'html2canvas';
import type { AxisValueFormatterContext } from '@mui/x-charts-pro';

interface BarChartProps {
	title: string;
	data: GraphDistribution[];
	xaxislabel: string;
	yaxislabel: string;
	registerExportFn?: (exportFn: () => void) => void;
	fullWidth?: boolean;
	chartSize: { width: number; height: number };
	currency?: boolean; // Optional: format values as currency
}


const numberFormatter = new Intl.NumberFormat('en-US', {
	notation: 'compact',
	maximumFractionDigits: 1,
});

const currencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
	maximumFractionDigits: 2,
});

const CircadiaBarChart: React.FC<BarChartProps> = ({
	title,
	data = [],
	xaxislabel,
	yaxislabel,
	registerExportFn,
	fullWidth = false,
	chartSize,
	currency = false,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));
	const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));

	const chartColorWheelValues: string[] = theme?.custom?.chartColorWheel || [];

	const transformedData = useMemo(() => {
		if (fullWidth || !data || data.length <= 5) {
			return data || [];
		}
		
		// Take top 5 items
		const topItems = data.slice(0, 5);
		
		// Aggregate remaining items
		const otherItems = data.slice(5);
		const otherValue = otherItems.reduce((sum, item) => sum + item.value, 0);
		
		// Create "Other" category
		const otherCategory: GraphDistribution = {
			id: -1, // Use -1 to ensure unique ID
			value: otherValue,
			label: "Other"
		};

		return [...topItems, otherCategory];
	}, [data, fullWidth]);
	
	const chartRef = useRef<HTMLDivElement>(null);

	// Responsive tick label font size
	const tickLabelFontSize = useMemo(() => {
		if (isMobile) return 9;
		if (isMd) return 10;
		if (isLgUp) return 10;
		return 11;
	}, [isMobile, isMd, isLgUp]);

	const handleExportChart = () => {
		if (chartRef.current) {
			html2canvas(chartRef.current, {
				allowTaint: true,
				useCORS: true,
				scale: 2,
				backgroundColor: '#fff', // Set background to white
				logging: true,
			}).then((canvas) => {
				try {
					const link = document.createElement('a');
					link.download = `${title.toLowerCase().replace(/\s+/g, '_')}.png`;
					link.href = canvas.toDataURL('image/png', 1.0);
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} catch (error) {
					console.error('Error exporting chart:', error);
				}
			});
		}
	};

	const tooltipValueFormatter = (value: any) => {
		return currency ? currencyFormatter.format(value) : numberFormatter.format(value);
	};

	const valueFormatterIgnoreEmpty = (value: any, context?: AxisValueFormatterContext) => {
		if (context && context.location === 'tick' && context.defaultTickLabel === '') {
			return '';
		}
		return numberFormatter.format(value);
	};

	useEffect(() => {
		if (registerExportFn) {
			registerExportFn(handleExportChart);
		}
	}, [registerExportFn]);

	return (
		<div ref={chartRef}>
			<BarChartPro
				data-testid={`${title.toLowerCase().replace(/\s+/g, '-')}-bar-chart`}
				className="circadia-bar-chart"
				dataset={transformedData.map((item) => ({
					...item,
				}))}
				xAxis={[
					{
						scaleType: 'band',
						dataKey: 'label',
						colorMap: {
							type: 'ordinal',
							colors: chartColorWheelValues,
						},
						label: xaxislabel,
						tickLabelStyle: {
							fontSize: tickLabelFontSize,
						},
						labelStyle: {
							fontSize: 14,
							fontWeight: 'bolder',
						},
					},
				]}
				yAxis={[
					{
						label: yaxislabel,
						valueFormatter: valueFormatterIgnoreEmpty,
						tickLabelStyle: { fontSize: tickLabelFontSize },
						labelStyle: {
							fontSize: 14,
							fontWeight: 'bolder',
						},
					},
				]}
				series={[
					{
						dataKey: 'value',
						valueFormatter: tooltipValueFormatter,
					},
				]}
				width={chartSize.width}
				height={chartSize.height}
				slotProps={{
					axisTickLabel: {
						fontSize: tickLabelFontSize,
					},
				}}
			/>
		</div>
	);
};

export default CircadiaBarChart;
